'use client'

import { FormField } from '@/components/form-section'
import { scheduleChatMessage } from '@/lib/api/sales/flows-api'
import { useEffect, useMemo, useState } from 'react'
import { MdScheduleSend } from 'react-icons/md'
import { toast } from 'react-toastify'

interface Props {
    isOpen: boolean
    chatId: number
    /** Texto que já estava no campo de mensagem. */
    initialMessage: string
    /** Setor da conversa na API oficial: fora de 24h a mensagem não sai. */
    officialApi: boolean
    onClose: () => void
    onScheduled: () => void
}

/** `YYYY-MM-DDTHH:mm` no horário local, formato do `<input type="datetime-local">`. */
const toLocalInput = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const MIN_AHEAD_MS = 5 * 60_000

function quickOptions(now: Date) {
    const inOneHour = new Date(now.getTime() + 60 * 60_000)
    const tomorrowMorning = new Date(now)
    tomorrowMorning.setDate(now.getDate() + 1)
    tomorrowMorning.setHours(9, 0, 0, 0)
    const tomorrowAfternoon = new Date(tomorrowMorning)
    tomorrowAfternoon.setHours(14, 0, 0, 0)
    return [
        { label: 'Em 1 hora', date: inOneHour },
        { label: 'Amanhã, 9h', date: tomorrowMorning },
        { label: 'Amanhã, 14h', date: tomorrowAfternoon },
    ]
}

/** "Agendar envio": a mensagem sai na data e hora escolhidas, pelo setor desta conversa. */
export default function ScheduleMessageModal({ isOpen, chatId, initialMessage, officialApi, onClose, onScheduled }: Props) {
    const [message, setMessage] = useState('')
    const [when, setWhen] = useState('')
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        setMessage(initialMessage)
        setWhen('')
    }, [isOpen, initialMessage])

    const options = useMemo(() => quickOptions(new Date()), [isOpen])

    if (!isOpen) return null

    const scheduledAt = when ? new Date(when) : null
    const tooSoon = !!scheduledAt && scheduledAt.getTime() < Date.now() + MIN_AHEAD_MS
    const canSubmit = !!message.trim() && !!scheduledAt && !tooSoon && !submitting

    const handleSubmit = async () => {
        if (!canSubmit || !scheduledAt) return
        setSubmitting(true)
        try {
            const res = await scheduleChatMessage({ chatId, message: message.trim(), scheduledAt: scheduledAt.toISOString() })
            if (res.success) {
                toast.success('Mensagem agendada')
                onScheduled()
            } else {
                toast.error(res.error || 'Não foi possível agendar a mensagem')
            }
        } catch {
            toast.error('Não foi possível agendar a mensagem')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-lg">
                <h3 className="mb-1 flex items-center gap-2 text-lg font-bold">
                    <MdScheduleSend className="text-secondary" /> Agendar envio
                </h3>
                <p className="mb-5 text-xs text-base-content/60">
                    A mensagem sai na data e hora escolhidas, pelo setor desta conversa. Até lá, ela aparece acima do campo de mensagem e pode ser cancelada.
                </p>

                <div className="flex flex-col gap-5">
                    <FormField label="Mensagem" htmlFor="schedule-message" required>
                        <textarea
                            id="schedule-message"
                            className="textarea textarea-bordered w-full text-sm leading-relaxed"
                            rows={5}
                            maxLength={4096}
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                        />
                    </FormField>

                    <FormField
                        label="Quando enviar"
                        htmlFor="schedule-when"
                        required
                        hint={tooSoon ? undefined : 'Pelo menos 5 minutos a partir de agora e no máximo 90 dias.'}
                    >
                        <div className="flex flex-wrap gap-2">
                            {options.map(option => (
                                <button
                                    key={option.label}
                                    type="button"
                                    className={`btn btn-sm ${when === toLocalInput(option.date) ? 'btn-secondary' : 'btn-outline'}`}
                                    onClick={() => setWhen(toLocalInput(option.date))}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        <input
                            id="schedule-when"
                            type="datetime-local"
                            className={`input input-bordered input-sm w-full ${tooSoon ? 'input-error' : ''}`}
                            min={toLocalInput(new Date(Date.now() + MIN_AHEAD_MS))}
                            value={when}
                            onChange={e => setWhen(e.target.value)}
                        />
                        {tooSoon && <p className="text-xs text-error">Escolha um horário pelo menos 5 minutos à frente.</p>}
                    </FormField>

                    {officialApi && (
                        <div role="alert" className="alert alert-warning py-2 text-xs leading-relaxed">
                            Este setor usa a API oficial: a mensagem só sai se o contato tiver escrito nas 24 horas antes do horário agendado. Fora disso, o envio é cancelado e o motivo fica no histórico de execuções.
                        </div>
                    )}
                </div>

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}>
                        Cancelar
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={handleSubmit} disabled={!canSubmit}>
                        {submitting ? <span className="loading loading-spinner loading-xs" /> : 'Agendar'}
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={() => !submitting && onClose()} />
        </div>
    )
}
